import {Injectable, Injector, Type} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {IStackPanelConfig, IStackPanelInstance, TStackPanelId} from './interface';
import {StackPanelRef} from './stack-panel-ref';
import {STACK_PANEL_CONFIG} from './tokens';

export interface IStackPanelView<T = unknown> extends IStackPanelInstance<T> {
  injector: Injector;
  ref: StackPanelRef<T>;
}

@Injectable({
  providedIn: 'root'
})
export class StackPanelService {
  private readonly panelsSubject: BehaviorSubject<IStackPanelView[]> = new BehaviorSubject<IStackPanelView[]>([]);
  readonly panels$: Observable<IStackPanelView[]> = this.panelsSubject.asObservable();

  private nextPanelId = 1;

  constructor(private injector: Injector) {
  }

  show<T>(component: Type<T>, config: IStackPanelConfig<T> = {}): StackPanelRef<T> {
    if (config.id !== undefined) {
      this.hide(config.id);
    }

    const panelId: TStackPanelId = config.id ?? this.nextPanelId++;
    const ref = new StackPanelRef<T>((id?: TStackPanelId) => this.hide(id), panelId);
    const parentInjector = config.parentInjector ?? this.injector;
    const panelInjector = Injector.create({
      parent: parentInjector,
      providers: [
        {provide: StackPanelRef, useValue: ref},
        {provide: STACK_PANEL_CONFIG, useValue: config},
      ],
    });

    const panel: IStackPanelView<T> = {
      id: panelId,
      component,
      config,
      initialState: config.initialState || {},
      panelClass: config.class,
      panelWidth: config.panelWidth,
      injector: panelInjector,
      ref,
    };

    this.panelsSubject.next([...this.panelsSubject.value, panel]);
    return ref;
  }

  hide(id?: TStackPanelId): void {
    const panels = this.panelsSubject.value;
    if (!panels.length) {
      return;
    }

    const panel = id === undefined
      ? panels[panels.length - 1]
      : panels.find((item) => item.id === id);
    if (!panel) {
      return;
    }
    if (!panel.ref.canHide()) {
      return;
    }

    panel.ref.onHide.next(panel.id);
    this.panelsSubject.next(panels.filter((item) => item !== panel));
    panel.ref.onHidden.next(panel.id);
    panel.ref.onHide.complete();
    panel.ref.onHidden.complete();
  }

  hideAll(): void {
    const panels = [...this.panelsSubject.value];
    panels.reverse().forEach((panel) => this.hide(panel.id));
  }

  getPanels(): IStackPanelView[] {
    return [...this.panelsSubject.value];
  }
}
